'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'

const DEFAULT_COOLDOWN_SECONDS = 60

interface UseOTPCountdownOptions {
  cooldownSeconds?: number
  onComplete?: () => void
}

interface UseOTPCountdownReturn {
  /** Seconds remaining until resend is allowed */
  secondsRemaining: number
  /** Whether the countdown is active (cannot resend yet) */
  isActive: boolean
  /** Start or restart the countdown */
  start: () => void
  /** Reset the countdown (stop it and set to 0) */
  reset: () => void
}

export function useOTPCountdown(options: UseOTPCountdownOptions = {}): UseOTPCountdownReturn {
  const { cooldownSeconds = DEFAULT_COOLDOWN_SECONDS, onComplete } = options
  const [secondsRemaining, setSecondsRemaining] = useState(0)

  useEffect(() => {
    if (secondsRemaining <= 0) return

    const timer = setInterval(() => {
      setSecondsRemaining(prev => {
        const next = prev - 1
        if (next <= 0) {
          clearInterval(timer)
          onComplete?.()
          return 0
        }
        return next
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [secondsRemaining, onComplete])

  const start = useCallback(() => {
    setSecondsRemaining(cooldownSeconds)
  }, [cooldownSeconds])

  const reset = useCallback(() => {
    setSecondsRemaining(0)
  }, [])

  const isActive = secondsRemaining > 0

  return useMemo(
    () => ({
      secondsRemaining,
      isActive,
      start,
      reset,
    }),
    [secondsRemaining, isActive, start, reset],
  )
}
